"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { MapPin, Building2, Star, FileCode, Newspaper, Target, Layers, SlidersHorizontal as Sliders, ChartLine as LineChart, ShieldCheck, Link2, CircleCheck as CheckCircle2, ArrowRight, Sparkles, PhoneCall, Calendar } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const localServices = [{
  id: "gbp-opt",
  title: "Google Business Profile Optimization",
  icon: MapPin,
  description: "Complete setup, category refinement, attribute tuning, and structured post management to maximize your visibility in Google's local Map Pack.",
  deliverable: "Top 3 Map Pack Visibility"
}, {
  id: "local-citations",
  title: "Local Citation & Directory Building",
  icon: Building2,
  description: "Consistent NAP (Name, Address, Phone) citation auditing and manual submission across high-authority UK and regional local directories.",
  deliverable: "100% Consistent NAP Data"
}, {
  id: "review-management",
  title: "Review & Reputation Strategy",
  icon: Star,
  description: "Structured workflows to gather genuine customer reviews and ethically improve your local star ratings on Google and key trust platforms.",
  deliverable: "Enhanced Trust Signals"
}, {
  id: "local-schema",
  title: "Local Schema & Structured Data",
  icon: FileCode,
  description: "Custom JSON-LD schema markup for LocalBusiness, GeoCoordinates, opening hours, and service areas to guide search engine crawlers precisely.",
  deliverable: "Rich Snippet Eligibility"
}, {
  id: "localized-content",
  title: "Hyperlocal Content Strategy",
  icon: Newspaper,
  description: "Crafting location-specific content tailored to local customer intent, regional terminology, and community search topics.",
  deliverable: "High-Intent Local Copy"
}, {
  id: "geo-landing-pages",
  title: "Geo-Targeted Landing Pages",
  icon: Target,
  description: "High-converting city and regional service pages designed to capture suburban and surrounding area organic search queries.",
  deliverable: "Scalable Regional Reach"
}, {
  id: "multi-location",
  title: "Multi-Location SEO Architecture",
  icon: Layers,
  description: "Hierarchical site architecture and canonical strategies tailored for businesses managing multiple physical branches or regional hubs.",
  deliverable: "Clean Location Hierarchy"
}, {
  id: "onpage-local",
  title: "On-Page Local Signal Tuning",
  icon: Sliders,
  description: "Precision optimization of local title tags, meta descriptions, localized header tags, and embedded Google Maps for maximum context.",
  deliverable: "Relevance Score Boost"
}, {
  id: "competitor-benchmark",
  title: "Competitor Local Benchmarking",
  icon: LineChart,
  description: "Deep competitor teardowns analysing local link velocity, review counts, proximity advantages, and hidden ranking signals.",
  deliverable: "Actionable Gap Analysis"
}, {
  id: "mappack-audit",
  title: "Local Map Pack Technical Audits",
  icon: ShieldCheck,
  description: "Comprehensive audits identifying profile suspensions, algorithmic suppressions, duplicate listings, and ranking blockers.",
  deliverable: "Clear Recovery Roadmap"
}, {
  id: "hyperlocal-links",
  title: "Hyperlocal Link Acquisition",
  icon: Link2,
  description: "Targeted outreach to local news publications, community blogs, sponsorships, and regional business partners for contextual backlinks.",
  deliverable: "Authority Local Backlinks"
}, {
  id: "local-cro",
  title: "Local Conversion Rate Optimization",
  icon: CheckCircle2,
  description: "Turning local clicks into phone calls and direction requests through prominent call buttons, clear map directions, and local trust badges.",
  deliverable: "Higher Lead Conversion"
}];
export default function Localseogrid() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.06
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };
  return <section id="localseoservicesgrid" data-nav="light" className="bg-background text-foreground py-16 md:py-24 relative overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Heading */}
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16 space-y-4">
          <Badge variant="outline" className="px-3 py-1 text-xs font-semibold uppercase tracking-wider border-primary/30 text-primary bg-primary/5 rounded-full">
            Direct Local SEO Expertise
          </Badge>
          
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-serif text-foreground tracking-tight">
            Comprehensive Local SEO Services Handled Personally by Me
          </h2>

          <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
            When you hire YE Consultancy for local SEO, you work directly with me, Yassine Essaadi. No account managers or outsourced teams—just direct hands-on SEO expertise to establish market dominance in your target geographic areas.
          </p>
        </div>

        {/* Grid of 12 Services */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.1
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {localServices.map((service, i) => {
          const IconComponent = service.icon;
          return <motion.div key={service.id} data-index={i} variants={itemVariants} className="h-full flex">
                <Card className="bg-card text-card-foreground border border-border shadow-md hover:shadow-lg transition-[box-shadow,border-color] duration-300 flex flex-col justify-between w-full overflow-hidden rounded-xl group hover:border-primary/40">
                  <div>
                    {/* Card Header Banner */}
                    <div className="bg-primary text-primary-foreground p-5 flex items-center gap-3.5 border-b border-primary/20">
                      <div className="p-2.5 bg-primary-foreground/15 rounded-lg text-primary-foreground shrink-0 border border-primary-foreground/20">
                        <IconComponent className="size-5" />
                      </div>
                      <h3 className="font-semibold text-base md:text-lg leading-snug text-primary-foreground font-serif">
                        {service.title}
                      </h3>
                    </div>

                    <CardContent className="pt-5 pb-4 px-6 space-y-4">
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {service.description}
                      </p>

                      <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-muted text-muted-foreground text-xs font-medium border border-border/60">
                        <Sparkles className="size-3.5 text-accent shrink-0" />
                        <span>{service.deliverable}</span>
                      </div>
                    </CardContent>
                  </div>

                  <div className="p-6 pt-0 mt-auto">
                    <Button variant="outline" size="sm" className="w-full justify-between group-hover:bg-primary group-hover:text-primary-foreground group-hover:border-primary transition-colors duration-200" asChild>
                      <Link to="/ContactPage">
                        <span>Book Local SEO Strategy</span>
                        <ArrowRight className="size-4 group-hover:translate-x-1 transition-transform duration-200" />
                      </Link>
                    </Button>
                  </div>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Bottom Consultation Banner */}
        <div className="mt-16 bg-muted/60 border border-border rounded-2xl p-8 md:p-12 text-center md:text-left flex flex-col md:flex-row items-center justify-between gap-8 shadow-sm">
          <div className="space-y-3 max-w-2xl">
            <div className="inline-flex items-center gap-2 text-primary font-semibold text-sm">
              <PhoneCall className="size-4" />
              <span>Direct Strategy Consultation</span>
            </div>
            <h3 className="text-2xl md:text-3xl font-bold font-serif text-foreground">
              Ready to dominate local search in your target cities?
            </h3>
            <p className="text-muted-foreground text-sm md:text-base leading-relaxed">
              Book a free consultation directly with Yassine Essaadi. We will review your current Google Business Profile, local keyword rankings, and competitor gaps.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-4 shrink-0 w-full md:w-auto">
            <Button size="lg" className="w-full sm:w-auto gap-2 bg-primary text-primary-foreground hover:bg-primary/90 shadow-md" asChild>
              <Link to="/ContactPage">
                <SafeHydrate><Calendar className="size-4" /></SafeHydrate>
                <span>Book Free SEO Consultation</span>
              </Link>
            </Button>

            <Button variant="outline" size="lg" className="w-full sm:w-auto gap-2 border-border" asChild>
              <Link to="/CaseStudiesPage">
                <span>Explore Local SEO Results</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}